import React, { useState, useEffect, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  IconButton, 
  Paper, 
  TextField, 
  Button, 
  Snackbar, 
  Alert, 
  Tabs, 
  Tab,
  Dialog,
  DialogContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  AppBar,
  Toolbar,
  Slide
} from '@mui/material';
import { ArrowBack, Save, ListAlt, DeleteSweep, Close } from '@mui/icons-material';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState({
    siteName: '',
    apiEndpoint: '',
  });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);

  // Ottieni i log in tempo reale
  const logs = useLiveQuery(() => db.logs.reverse().toArray()) || [];

  useEffect(() => {
    const loadSettings = async () => {
      const siteName = await db.genericTable.get({ table: 'settings', field: 'siteName' });
      const apiEndpoint = await db.genericTable.get({ table: 'settings', field: 'apiEndpoint' });
      
      setSettings({
        siteName: siteName ? siteName.value : '',
        apiEndpoint: apiEndpoint ? apiEndpoint.value : '',
      });
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      await db.genericTable.put({ table: 'settings', field: 'siteName', value: settings.siteName });
      await db.genericTable.put({ table: 'settings', field: 'apiEndpoint', value: settings.apiEndpoint });
      
      // Aggiungi un log del salvataggio
      await db.logs.add({
        timestamp: new Date().toISOString(),
        type: 'INFO',
        message: 'Impostazioni aggiornate',
        details: `SiteName: ${settings.siteName}, API: ${settings.apiEndpoint}`
      });

      setToast({ open: true, message: 'Impostazioni salvate con successo!', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante il salvataggio.', severity: 'error' });
    }
  };

  const clearLogs = async () => {
    if (window.confirm('Sei sicuro di voler eliminare tutti i log?')) {
      await db.logs.clear();
      setToast({ open: true, message: 'Log eliminati.', severity: 'info' });
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/')} aria-label="back" sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Impostazioni
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 4, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          indicatorColor="primary" 
          textColor="primary"
          centered
          sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <Tab label="Strumenti & Utility" />
          <Tab label="Configurazione Generale" />
        </Tabs>

        {/* Tab 0: Strumenti */}
        {activeTab === 0 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Utility di Sistema</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Button 
                  fullWidth
                  variant="outlined" 
                  startIcon={<ListAlt />} 
                  onClick={() => setIsLogModalOpen(true)}
                  sx={{ height: '80px', borderRadius: 2 }}
                >
                  Visualizza Log di Sistema
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Configurazione */}
        {activeTab === 1 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Parametri Applicazione</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '600px' }}>
              <TextField
                label="Nome del Sito"
                variant="outlined"
                fullWidth
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
              />
              <TextField
                label="API Endpoint"
                variant="outlined"
                fullWidth
                value={settings.apiEndpoint}
                onChange={(e) => setSettings({ ...settings, apiEndpoint: e.target.value })}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ alignSelf: 'flex-start', mt: 1 }}
              >
                Salva Modifiche
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Modal Log Fullscreen */}
      <Dialog 
        fullScreen
        open={isLogModalOpen} 
        onClose={() => setIsLogModalOpen(false)}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setIsLogModalOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Log di Sistema
            </Typography>
            <Button 
              color="inherit" 
              startIcon={<DeleteSweep />} 
              onClick={clearLogs}
            >
              Svuota Log
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent dividers sx={{ backgroundColor: 'background.default' }}>
          <List sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>
            {logs.length === 0 ? (
              <Typography align="center" sx={{ py: 10, opacity: 0.5 }}>Nessun log disponibile.</Typography>
            ) : (
              logs.map((log) => (
                <ListItem 
                  key={log.id} 
                  divider 
                  sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    py: 2,
                    '&:last-child': { borderBottom: 'none' }
                  }}
                >
                  <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mb: 1 }}>
                    <Chip 
                      label={log.type} 
                      size="small" 
                      color={log.type === 'ERROR' ? 'error' : 'primary'} 
                      variant="outlined"
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {new Date(log.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  <ListItemText 
                    primary={log.message} 
                    secondary={log.details}
                    primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '1.1rem', mb: 0.5 } }}
                    secondaryTypographyProps={{ variant: 'body2', sx: { whiteSpace: 'pre-wrap', fontFamily: 'monospace' } }}
                  />
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Settings;
